import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar as CalendarIcon, ExternalLink, Phone } from 'lucide-react';

const ClientRow = ({ client, group, navigate }) => (
  <div
    className={`flex flex-col gap-1 p-2 rounded-lg border ${group.cardBg} hover:shadow-sm transition-all cursor-pointer text-xs`}
    onClick={() => client.file_id && navigate(`/files/${client.file_id}`)}
  >
    <div className="flex items-center gap-1.5">
      <div className={`w-1.5 h-1.5 rounded-full ${group.dotColor} shrink-0`} />
      <span className="font-semibold text-gray-900 truncate flex-1">{client.client_name}</span>
      <span className="font-bold text-indigo-700 text-[10px] shrink-0">{client.progress}%</span>
    </div>
    <div className="flex items-center gap-1.5 pl-3">
      <div className="w-full bg-gray-200 rounded-full h-1">
        <div className={`bg-gradient-to-r ${group.barColor} h-1 rounded-full`} style={{ width: `${client.progress}%` }} />
      </div>
    </div>
    <div className="flex items-center justify-between pl-3">
      {client.file_number && <span className="text-gray-400 text-[9px]">{client.file_number}</span>}
      {client.deadline_info && client.deadline_info.status !== 'non_defini' && (
        <span className={`text-[9px] font-bold px-1 py-0.5 rounded ${
          client.deadline_info.color === 'red' ? 'bg-red-100 text-red-700' :
          client.deadline_info.color === 'orange' ? 'bg-orange-100 text-orange-700' :
          'bg-green-100 text-green-700'
        }`}>
          {client.deadline_info.is_late ? `Retard ${Math.round(client.deadline_info.delay_hours)}h` : `${Math.round(client.deadline_info.remaining_hours)}h restant`}
        </span>
      )}
      {client.assigned_employee && (
        <span className="text-[9px] text-blue-600 font-medium truncate max-w-[80px]">{client.assigned_employee}</span>
      )}
    </div>
  </div>
);

const ScheduleSection = ({ schedule }) => {
  const navigate = useNavigate();

  const { urgentClients, moyenClients, faibleClients } = useMemo(() => {
    if (!schedule) return { urgentClients: [], moyenClients: [], faibleClients: [] };
    return {
      urgentClients: schedule.clients.filter(c => c.priority === 'urgent' || c.priority === 'haute'),
      moyenClients: schedule.clients.filter(c => !c.priority || c.priority === 'moyenne' || c.priority === 'moyen'),
      faibleClients: schedule.clients.filter(c => c.priority === 'basse' || c.priority === 'faible'),
    };
  }, [schedule]);

  if (!schedule) return null;

  const groups = [
    { key: 'urgent', label: 'Urgent', clients: urgentClients, dotColor: 'bg-red-500', badgeBg: 'bg-red-600', cardBg: 'bg-red-50 border-red-200', barColor: 'from-red-500 to-orange-500', headerBg: 'from-red-50 to-red-100', textColor: 'text-red-700' },
    { key: 'moyen', label: 'Moyen', clients: moyenClients, dotColor: 'bg-amber-500', badgeBg: 'bg-amber-500', cardBg: 'bg-amber-50 border-amber-200', barColor: 'from-amber-400 to-yellow-400', headerBg: 'from-amber-50 to-yellow-50', textColor: 'text-amber-700' },
    { key: 'faible', label: 'Faible', clients: faibleClients, dotColor: 'bg-emerald-500', badgeBg: 'bg-emerald-500', cardBg: 'bg-emerald-50 border-emerald-200', barColor: 'from-emerald-400 to-green-400', headerBg: 'from-emerald-50 to-green-50', textColor: 'text-emerald-700' },
  ];

  return (
    <Card className="border-0 shadow-xl bg-white" data-testid="schedule-today-section">
      <CardHeader className="pb-2 pt-4 px-5">
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2 text-lg">
            <CalendarIcon className="h-5 w-5 text-indigo-600" />
            {`Schedule d'aujourd'hui`}
          </CardTitle>
          <div className="flex gap-2">
            <Badge className="bg-indigo-100 text-indigo-800 text-xs">{schedule.total_clients} client(s)</Badge>
            <Badge className="bg-blue-100 text-blue-800 text-xs">{schedule.total_tasks} tache(s)</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-5 pb-4">
        {schedule.clients.length > 0 ? (
          <div className="grid grid-cols-3 gap-3" data-testid="schedule-columns">
            {groups.map(group => (
              <div key={group.key} className={`rounded-xl bg-gradient-to-b ${group.headerBg} p-3`} data-testid={`schedule-col-${group.key}`}>
                {/* Column header */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <div className={`w-2.5 h-2.5 rounded-full ${group.dotColor} ${group.key === 'urgent' ? 'animate-pulse' : ''}`} />
                    <span className={`font-bold text-sm ${group.textColor}`}>{group.label}</span>
                  </div>
                  <Badge className={`${group.badgeBg} text-white text-xs px-1.5 py-0`}>{group.clients.length}</Badge>
                </div>
                {/* Client list */}
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {group.clients.length > 0 ? group.clients.map((client, idx) => (
                    <ClientRow key={client.file_id || `${group.key}-${idx}`} client={client} group={group} navigate={navigate} />
                  )) : (
                    <p className="text-xs text-gray-400 text-center py-3">Aucun</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-gray-500">
            <CalendarIcon className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Aucun client assigne</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ScheduleSection;
